import React from 'react';
import classNames from 'classnames';

import { Text } from 'components/Text';
import { Toggle } from 'components/Toggle';
import { customEmailTemplateTypes } from './data/templates-data';
import { limitText } from 'utils';

export type KlaviyoFlowForEventType = {
  flowId: string;
  status: string;
  sendCount?: number;
};

export type EmailNotificationTemplateType = {
  key: string;
  text: string;
  subText: string;
  imgSrc?: string;
  icon?: React.FC<any>;
};

type EmailNotificationTemplateProps = {
  userTemplates: any[];
  isLoading: boolean;
  onChangeActiveTemplate: any;
  embedded?: boolean;
  typeMap?: Record<string, EmailNotificationTemplateType>;
  klaviyoFlowsByEventType?: Record<string, KlaviyoFlowForEventType>;
  onToggleKlaviyoFlow?: (
    flow: KlaviyoFlowForEventType,
    turnOn: boolean,
  ) => void;
  togglingKlaviyoFlowId?: string | null;
};

export const EmailNotificationTemplates = ({
  userTemplates,
  isLoading,
  onChangeActiveTemplate,
  embedded = false,
  typeMap = customEmailTemplateTypes,
  klaviyoFlowsByEventType,
  onToggleKlaviyoFlow,
  togglingKlaviyoFlowId,
}: EmailNotificationTemplateProps) => {
  return (
    <div className={classNames(!embedded && 'mt-16')}>
      <div className="flex flex-col">
        <div className={classNames(!embedded && '-my-2 sm:-mx-6 lg:-mx-8')}>
          <div
            className={classNames(
              'py-2 align-middle inline-block w-full',
              !embedded && 'sm:px-6 lg:px-8',
            )}
          >
            <div
              className={classNames(
                'overflow-hidden border border-[#e1e3e5] bg-white shadow-sm',
                embedded ? 'rounded-xl' : 'sm:rounded-lg',
              )}
            >
              <table className="min-w-full table-fixed divide-y divide-[#e5e7eb]">
                <thead className="bg-[#f8fafc]">
                  <tr>
                    <th className="w-[62%] px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[#64748b]">
                      Notification
                    </th>
                    <th className="w-[16%] px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[#64748b]">
                      Volume
                    </th>
                    <th className="w-[22%] px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[#64748b]">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-[#e5e7eb]">
                  {userTemplates.map((template) => {
                    const type = Object.values(typeMap).find(
                      (type) => type?.key === template.eventType,
                    );
                    const Icon = type?.icon;
                    const klaviyoFlow =
                      klaviyoFlowsByEventType?.[template.eventType];
                    return (
                      <tr className="table-data-row" key={template.id}>
                        <td
                          key={template.id + 1}
                          className="px-6 py-5 text-sm font-medium text-gray-600"
                        >
                          <div className="flex min-w-0 items-center gap-4">
                            {Icon ? (
                              <div
                                style={{ width: 56, height: 56 }}
                                className="inline-flex flex-shrink-0 items-center justify-center rounded-xl border border-[#e2e8f0] bg-[#f8fafc]"
                              >
                                <Icon className="h-6 w-6 text-[#334155]" />
                              </div>
                            ) : (
                              <img
                                style={{ width: 56, height: 56 }}
                                className="inline-block flex-shrink-0 rounded-xl border border-[#e2e8f0] bg-[#f8fafc] object-cover"
                                src={type?.imgSrc ?? ''}
                                alt={template.eventType ?? 'Email template'}
                                referrerPolicy="no-referrer"
                              />
                            )}
                            <div className="min-w-0 whitespace-normal">
                              <Text
                                value={limitText(type?.text)}
                                type="bold"
                                className="break-words text-base leading-6 text-[#0f172a]"
                              />
                              <Text
                                value={limitText(type?.subText)}
                                type="sm"
                                className="mt-1 break-words leading-5 text-[#64748b]"
                              />
                            </div>
                          </div>
                        </td>
                        <td
                          key={template.id + 2}
                          className="px-6 py-5 whitespace-nowrap text-gray-600 text-sm font-medium"
                        >
                          <Text
                            value={`${
                              klaviyoFlow
                                ? klaviyoFlow.sendCount ?? 0
                                : template.sentCount ?? 0
                            }`}
                            type="sm"
                            className="text-[#0f172a] font-semibold"
                          />
                          <Text
                            value={'Emails sent'}
                            type="sm"
                            className="text-[#64748b]"
                          />
                        </td>
                        <td
                          key={template.id + 3}
                          className="px-6 py-5 whitespace-nowrap text-gray-600 text-sm font-medium"
                        >
                          <Toggle
                            key={template.id + 5}
                            isEnabled={
                              klaviyoFlow
                                ? klaviyoFlow.status === 'live'
                                : template.active
                            }
                            disabled={
                              !!klaviyoFlow &&
                              togglingKlaviyoFlowId === klaviyoFlow.flowId
                            }
                            onChange={(turnOn) =>
                              klaviyoFlow
                                ? onToggleKlaviyoFlow?.(klaviyoFlow, turnOn)
                                : onChangeActiveTemplate(template)
                            }
                            leftLabel={klaviyoFlow ? 'Draft' : undefined}
                            rightLabel={klaviyoFlow ? 'Live' : undefined}
                            isSmall
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
